import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { PlusIcon, TrashIcon, PencilIcon, MapPinIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { createLocationAsync, updateLocationAsync, deleteLocationAsync } from '../store/locationsSlice';
import { Location } from '../types';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';

interface LocationFormData {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  color: string;
}

// Predefined color palette for quick selection
const colorPalette = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#22C55E', // Green
  '#8B5CF6', // Purple
  '#F59E0B', // Amber
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#14B8A6', // Teal
];

export default function Locations() {
  const dispatch = useAppDispatch();
  const { locations, loading } = useAppSelector((state) => state.locations);
  const currentUser = useAppSelector((state) => state.users.currentUser);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, reset, setValue, watch } = useForm<LocationFormData>();
  const selectedColor = watch('color');

  const handleOpenModal = (location?: Location) => {
    if (location) {
      setEditingLocation(location);
      // Find an available color if location doesn't have one
      const usedColors = locations.filter(l => l.id !== location.id).map(loc => loc.color);
      const defaultColor = colorPalette.find(c => !usedColors.includes(c)) || colorPalette[0];
      reset({
        name: location.name,
        address: location.address,
        city: location.city,
        state: location.state,
        zipCode: location.zipCode,
        color: location.color || defaultColor,
      });
    } else {
      setEditingLocation(null);
      // Pick a default color that's not already in use
      const usedColors = locations.map(loc => loc.color);
      const availableColor = colorPalette.find(c => !usedColors.includes(c)) || colorPalette[0];
      reset({
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        color: availableColor,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLocation(null);
    reset();
  };

  const onSubmit = async (data: LocationFormData) => {
    setIsSaving(true);

    try {
      if (editingLocation) {
        const updatedLocation: Location = {
          ...editingLocation,
          name: data.name,
          address: data.address,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          color: data.color,
        };
        await dispatch(updateLocationAsync(updatedLocation)).unwrap();
      } else {
        const newLocation: Location = {
          id: `loc-${Date.now()}`,
          name: data.name,
          address: data.address,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          color: data.color,
        };
        await dispatch(createLocationAsync(newLocation)).unwrap();
      }

      handleCloseModal();
    } catch (error) {
      console.error('Failed to save location:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (location: Location) => {
    if (confirm(`Are you sure you want to remove ${location.name}?`)) {
      try {
        await dispatch(deleteLocationAsync(location.id)).unwrap();
      } catch (error) {
        console.error('Failed to delete location:', error);
        alert('Failed to delete. Please try again.');
      }
    }
  };

  const canManageLocation = (locationId: string) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    if (currentUser.role === 'site-admin') {
      return currentUser.locationAccess.includes(locationId);
    }
    return false;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/settings"
            className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 text-secondary-600 dark:text-secondary-400 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Locations</h1>
            <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
              Manage your facility locations
            </p>
          </div>
        </div>
        {currentUser?.role === 'admin' && (
          <Button onClick={() => handleOpenModal()} disabled={loading}>
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Location
          </Button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-secondary-600 dark:text-secondary-400">Loading...</span>
        </div>
      )}

      {/* Locations Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {locations.map((location) => {
          const locationColor = location.color || '#6B7280'; // Fallback to gray if no color
          return (
          <div 
            key={location.id}
            className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border-l-4 p-6"
            style={{ borderLeftColor: locationColor }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div 
                  className="flex-shrink-0 p-2 rounded-lg"
                  style={{ backgroundColor: `${locationColor}20` }}
                >
                  <MapPinIcon 
                    className="w-6 h-6" 
                    style={{ color: locationColor }}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
                      {location.name}
                    </h3>
                    <span 
                      className="w-4 h-4 rounded-full border-2 border-white dark:border-secondary-700 shadow-sm" 
                      style={{ backgroundColor: locationColor }}
                      title={`Location color: ${locationColor}`}
                    />
                  </div>
                  <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
                    {location.address}
                  </p>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    {location.city}, {location.state} {location.zipCode}
                  </p>
                </div>
              </div>
            </div>
            {canManageLocation(location.id) && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                <button
                  onClick={() => handleOpenModal(location)}
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50"
                  disabled={loading}
                >
                  <PencilIcon className="w-4 h-4 mr-2" />
                  Edit
                </button>
                {currentUser?.role === 'admin' && (
                  <button
                    onClick={() => handleDelete(location)}
                    className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-danger bg-red-50 dark:bg-red-900/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50"
                    disabled={loading}
                  >
                    <TrashIcon className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>

      {locations.length === 0 && !loading && (
        <Card>
          <div className="py-12 text-center">
            <MapPinIcon className="w-12 h-12 mx-auto text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-100">No locations</h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              Get started by adding a new location.
            </p>
          </div>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingLocation ? 'Edit Location' : 'Add Location'}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={isSaving}>
              {isSaving ? 'Saving...' : editingLocation ? 'Update' : 'Add'}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Location Name *
            </label>
            <input
              type="text"
              {...register('name', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., NNSC"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Address *
            </label>
            <input
              type="text"
              {...register('address', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Street address"
              disabled={isSaving}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                City *
              </label>
              <input
                type="text"
                {...register('city', { required: true })}
                className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isSaving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                State *
              </label>
              <input
                type="text"
                {...register('state', { required: true })}
                className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="NV"
                disabled={isSaving}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              ZIP Code *
            </label>
            <input
              type="text"
              {...register('zipCode', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Location Color *
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                {...register('color', { required: true })}
                className="w-12 h-10 p-1 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 cursor-pointer"
                disabled={isSaving}
              />
              <div className="flex flex-wrap gap-2">
                {colorPalette.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setValue('color', color)}
                    className={`w-6 h-6 rounded-full border-2 hover:scale-110 transition-transform ${
                      selectedColor === color 
                        ? 'border-secondary-900 dark:border-white ring-2 ring-offset-1 ring-secondary-400' 
                        : 'border-secondary-300 dark:border-secondary-600'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                    disabled={isSaving}
                  />
                ))}
              </div>
            </div>
            <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
              Choose a color or pick from presets. Keys from this location will use this color.
            </p>
          </div>
        </form>
      </Modal>
    </div>
  );
}
